import type { SearchOrganizationQuery } from '@repo/api-kit/graphql/types';
import { api as registrationApi } from '@repo/api-kit/registration';
import type { ApiCompany } from '#company/types';
import type { ApiCustomer } from '#customer/types';
import type { CurrentOrganizationAvatar } from '#avatar/types';
import type { ApiGetFeaturesResponse } from '#features/types';
import type { ApiOrganization } from '#organization-core/types';
import type { ApiGetKybResponse } from '#kyb/types';
import { adaptGraphQLKybForFromJSON } from '#kyb/api';

const { ApiCompany: ApiCompanyFromJSON } = registrationApi.company.firstparty.v1;
const { ApiCustomer: ApiCustomerFromJSON } = registrationApi.customer.firstparty.v1;
const { ApiGetFeaturesResponse: ApiGetFeaturesResponseFromJSON } =
  registrationApi.organizations.firstparty.v5;
const { ApiOrganization: ApiOrganizationFromJSON } = registrationApi.organizations.firstparty.v5;

/**
 * GraphQL organization response type (extracted from SearchOrganizationQuery)
 */
type GraphQLOrganization = SearchOrganizationQuery['searchOrganization']['organization'];

/**
 * Result of adapting GraphQL organization to domain types.
 * Each slice is typed with its corresponding domain type.
 */
export interface DomainOrganizationSlices {
  company: ApiCompany;
  customer: ApiCustomer;
  features: ApiGetFeaturesResponse;
  organizationCore: ApiOrganization;
  avatar: CurrentOrganizationAvatar;
  kyb: ApiGetKybResponse;
}

/**
 * Adapts GraphQL organization avatar to CurrentOrganizationAvatar type.
 */
function adaptAvatarForDomain(avatar: GraphQLOrganization['avatar']) {
  return {
    url: avatar.url ?? null,
    thumbUrl: avatar.thumbUrl ?? null,
    isDefault: avatar.isDefault ?? null,
  };
}

/**
 * Adapts GraphQL company fields for fromJSON parsing.
 * GraphQL returns uppercase enum values for address type (e.g., 'STANDARD'),
 * but the API fromJSON expects lowercase values (e.g., 'standard').
 * GraphQL also returns 'zipCode' (camelCase) but fromJSON expects 'zipcode' (lowercase).
 */
export function adaptCompanyForFromJSON(companyInput: GraphQLOrganization['company']) {
  return {
    ...companyInput,
    addresses: companyInput.addresses?.map(addr =>
      addr
        ? {
            ...addr,
            zipcode: addr.zipCode,
            type: addr.type.toLowerCase(),
          }
        : addr
    ),
  };
}

/**
 * Adapts GraphQL organization core fields for fromJSON parsing.
 * GraphQL returns uppercase enum values (e.g., 'ACTIVATED'), but the API fromJSON
 * expects lowercase values (e.g., 'activated').
 */
function adaptOrganizationCoreForFromJSON(graphqlOrganization: GraphQLOrganization) {
  return {
    ...graphqlOrganization,
    status: graphqlOrganization.status.toLowerCase(),
    suspensionReason: graphqlOrganization.suspensionReason?.toLowerCase() ?? null,
  };
}

/**
 * Adapts a GraphQL organization response to domain-typed slices.
 * Uses the fromJSON() helpers from api-kit to properly serialize each slice.
 *
 * @param graphqlOrganization - The organization data from searchOrganization GraphQL query
 * @returns Object containing all five domain-typed slices
 */
export function adaptGraphQLOrganizationToDomain(
  graphqlOrganization: GraphQLOrganization
): DomainOrganizationSlices {
  const companyInput = adaptCompanyForFromJSON(graphqlOrganization.company);
  const customerInput = graphqlOrganization.customer;
  const featuresInput = graphqlOrganization.features;
  const kybInput = graphqlOrganization.kyb;
  const organizationCoreInput = adaptOrganizationCoreForFromJSON(graphqlOrganization);
  const avatar = adaptAvatarForDomain(graphqlOrganization.avatar);

  if (!kybInput) {
    throw new Error('KYB data is missing from organization response');
  }

  return {
    company: ApiCompanyFromJSON.fromJSON(companyInput),
    customer: ApiCustomerFromJSON.fromJSON(customerInput),
    features: ApiGetFeaturesResponseFromJSON.fromJSON(featuresInput),
    organizationCore: ApiOrganizationFromJSON.fromJSON(organizationCoreInput),
    avatar,
    kyb: adaptGraphQLKybForFromJSON(kybInput),
  };
}
